"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Chip } from "../ui/Chip";
import { FiTrendingUp } from "react-icons/fi";

interface ChipsFormIconProps {
    onTagClick?: (tagName: string) => void;
    tags?: string[];
}

const defaultChips = [
    "நரேந்திர மோடி",
    "தேர்தல்கள்",
    "ஐபிஎல் 2023",
    "சமீபத்திய செய்திகள்",
    "முக்கிய செய்திகள்",
    "அரசியல்",
];

// Use Next.js rewrite proxy (configured in next.config.ts)
// This proxies /api/v1/* to https://dinamani.quintype.io/api/v1/*
const TRENDING_TAGS_API = "/api/v1/trending/tags";

export default function ChipsFormIconBuilder({ onTagClick, tags }: ChipsFormIconProps) {
    const router = useRouter();
    const [chips, setChips] = useState<string[]>(tags && tags.length > 0 ? tags : []);
    const [isLoading, setIsLoading] = useState(!(tags && tags.length > 0));

    useEffect(() => {
        // If tags are provided as props, use them
        if (tags && tags.length > 0) {
            setChips(tags);
            setIsLoading(false);
            return;
        }

        // Fetch from proxied API (no CORS issues)
        const fetchTags = async () => {
            try {
                const response = await fetch(`${TRENDING_TAGS_API}?period=day&limit=8`);

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                const data = await response.json();

                if (data?.tags && data.tags.length > 0) {
                    const tagNames = data.tags.map((tag: { name: string }) => tag.name);
                    setChips(tagNames);
                } else {
                    setChips(defaultChips);
                }
            } catch (error) {
                console.error("[ChipsFormIconBuilder] Error:", error);
                setChips(defaultChips);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTags();
    }, [tags]);

    const highlightedChip = chips[0];

    // Show loading skeleton
    if (isLoading) {
        return (
            <div className="flex flex-wrap gap-3 p-2">
                {[1, 2, 3, 4, 5, 6,7,8].map((i) => (
                    <div key={i} className="h-8 w-24 bg-gray-200 rounded-full animate-pulse" />
                ))}
            </div>
        );
    }

    // Show default chips if nothing loaded
    const displayChips = chips.length > 0 ? chips : defaultChips;

    return (
        <div className="flex flex-wrap gap-3 p-2">
            {displayChips.slice(0, 8).map((chipName) => (
                <div key={chipName} className="flex items-center overflow-hidden">
                    <Chip
                        onClick={() => {
                            if (onTagClick) {
                                onTagClick(chipName);
                            } else {
                                router.push(`/topic/${encodeURIComponent(chipName)}`);
                            }
                        }}
                        label={
                            <div className="flex items-center gap-2">
                                {chipName}
                                <FiTrendingUp className="text-sm text-red-500" />
                            </div>
                        }
                        active={chipName === highlightedChip}
                        className="bg-012061 text-12-tamil-400 hover:bg-[#E3E4E8]"
                    />
                </div>
            ))}
        </div>
    );

}
